'use client';

import useLocalStorage from '@/hooks/useLocalStorage';
import { Page } from '@/hooks/usePage';
import { Subject } from '@/hooks/useSubject';
import { slugify } from '@/utils/strings';
import { mdiArrowRightCircle } from '@mdi/js';
import { useCallback, useLayoutEffect, useState } from 'react';
import { EditorBlock } from '@/components/Redactie/Page/PagesEditor';
import Timeline from './Timeline';
import { useReducedMotion, motion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import TextButton from '@/components/Button/TextButton';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { useStrings } from '@/resources/translations/translations';
import endImage from './end.svg';
import Image from 'next/image';
import { measure } from '../Analytics/Analytics';

const AccessibleTimeline = ({
    subject,
    page,
    nextPageId,
    nextChapter,
    blocks,
}: {
    subject: Subject;
    page: Page;
    nextPageId?: number | null;
    nextChapter?: {
        id: number;
        title: string;
    } | null;
    blocks: Array<
        {
            id?: number;
            type: string;
            data: Record<string, never>;
            order: number;
            editor_only: boolean;
        } & EditorBlock
    >;
}) => {
    const reduceMotion = useReducedMotion();
    const router = useRouter();
    const strings = useStrings('components.AccessibleTimeline');

    const { subjectProgress, setPageProgress } = useSubjectProgress(subject.id);

    // Is rendered
    const [canShowTimeline, setCanShowTimeline] = useState(false);
    useLayoutEffect(() => {
        setCanShowTimeline(true);
    }, []);

    // Is finished
    const [isFinished, setIsFinished] = useState(false);
    useLayoutEffect(() => {
        setIsFinished(false);
    }, [page.id]);

    // Screenreader announcements
    const [isScreenReaderAnnouncementsEnabledLocalStorage] = useLocalStorage(
        'isScreenReaderAnnouncementsEnabled',
        true,
    );
    const [
        isScreenReaderAnnouncementsEnabled,
        setIsScreenReaderAnnouncementsEnabled,
    ] = useState(true);

    // Animated chat
    const [isAnimated, setIsAnimated] = useState(true);
    const [isStaticChatTimelineEnabled] = useLocalStorage(
        'isStaticChatTimelineEnabled',
        false,
    );

    // Scroll down when content height is changed
    const onContentHeightChanged = useCallback(() => {
        const scroller = document.querySelector('#accessibletimelinescroller');
        if (scroller && isAnimated) {
            if (reduceMotion) {
                scroller.scrollTop = scroller.scrollHeight;
            } else {
                scroller.scrollTo({
                    top: scroller.scrollHeight,
                    behavior: 'smooth',
                });
            }
        }
    }, [isAnimated, reduceMotion]);

    // Use layout effect to prevent 'html doesn't match server' error
    useLayoutEffect(() => {
        const hasFinishedPage = subjectProgress.pages?.[page.id];
        setIsAnimated(!isStaticChatTimelineEnabled && !hasFinishedPage);
        setIsScreenReaderAnnouncementsEnabled(
            isScreenReaderAnnouncementsEnabledLocalStorage,
        );
    }, [
        isStaticChatTimelineEnabled,
        isScreenReaderAnnouncementsEnabledLocalStorage,
    ]);

    // Next page url
    const nextPageUrl =
        !!nextChapter && !!nextPageId
            ? `/story/${subject.id}/${slugify(subject.title)}/${
                  nextChapter.id
              }/${slugify(nextChapter.title)}/${nextPageId}`
            : null;

    // Prefetch next page
    useLayoutEffect(() => {
        if (nextPageUrl) {
            router.prefetch(nextPageUrl);
        }
    }, [nextPageUrl]);

    return (
        <>
            {/* Start timeline if intro is finished */}
            {canShowTimeline && subjectProgress.hasSeenIntro && (
                <Timeline
                    subjectId={subject.id}
                    chapterId={page.chapter_id}
                    pageId={page.id}
                    blocks={blocks}
                    isAnimated={isAnimated}
                    isScreenReaderAnnouncementsEnabled={
                        isScreenReaderAnnouncementsEnabled
                    }
                    onContentHeightChanged={onContentHeightChanged}
                    onFinished={() => {
                        setIsFinished(true);
                        setPageProgress(page.id, true);
                        setTimeout(() => {
                            onContentHeightChanged();
                        }, 500);
                    }}
                />
            )}

            {/* Show button to continue story if timeline is finished and there is a next page */}
            {isFinished && (
                <div className="relative">
                    <div className="relative z-10">
                        <TextButton
                            icon={mdiArrowRightCircle}
                            className={
                                nextPageUrl ? 'mt-8' : 'mt-10 translate-y-8'
                            }
                            onClick={() => {
                                measure(
                                    `${
                                        nextPageUrl
                                            ? 'story_continue'
                                            : 'story_finish'
                                    }_${subject.id}_${page.id}`,
                                    'navigation',
                                );
                                router.push(
                                    nextPageUrl ??
                                        `/?finished-story=${subject.id}`,
                                );
                            }}
                        >
                            {strings(
                                nextPageUrl ? 'next_button' : 'finished_button',
                            )}
                        </TextButton>
                    </div>
                    {!nextPageUrl && (
                        <motion.div
                            className="-z-1"
                            initial={{
                                opacity: 0,
                            }}
                            animate={{
                                opacity: 1,
                                transition: { delay: 0.5, duration: 2 },
                            }}
                            aria-hidden
                        >
                            <Image
                                src={endImage}
                                alt=""
                                aria-hidden
                                className="fixed -bottom-0 -inset-x-0 w-full aspect[2/1] -z-1 overflow-visible scale-105 translate-y-16"
                            />
                        </motion.div>
                    )}
                </div>
            )}
        </>
    );
};

export default AccessibleTimeline;
